import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtMoney, fmtQty, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ShoppingCart, FileText, Play, Clock } from "lucide-react";

export default function Mrp() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [groups, setGroups] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [runs, setRuns] = useState([]);

  const load = useCallback(() => {
    api.get("/mrp/suggestions").then((r) => setGroups(r.data));
    api.get("/mrp/schedule").then((r) => setSchedule(r.data));
    api.get("/mrp/runs").then((r) => setRuns(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const generatePo = async (g) => {
    try {
      const { data } = await api.post("/mrp/purchase-orders", { item_numbers: g.lines.map((l) => l.item_number) });
      toast.success(`${t("mrp.poCreated")}: ${data.created.map((c) => c.number).join(", ")}`);
      (data.warnings || []).forEach((w) => toast.warning(w));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const generateReq = async (g) => {
    try {
      const { data } = await api.post("/stock-alerts/requisition", { item_numbers: g.lines.map((l) => l.item_number) });
      toast.success(`${t("al.reqCreated")}: ${data.number}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runNow = async () => {
    try {
      const { data } = await api.post("/mrp/run");
      toast.success(`${t("mrp.runDone")}: ${data.po_count} OC`);
      (data.warnings || []).forEach((w) => toast.warning(w));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveSchedule = async (patch) => {
    const next = { ...schedule, ...patch };
    setSchedule(next);
    try {
      const { data } = await api.put("/mrp/schedule", { enabled: next.enabled, interval_hours: Number(next.interval_hours) });
      setSchedule(data);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!groups || !schedule) return <div className="text-xs text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div data-testid="mrp-page">
      <PageHeader title={t("mrp.title")} subtitle={t("mrp.subtitle")}>
        {can("admin", "buyer") && (
          <Button size="sm" onClick={runNow} className="rounded-none text-xs font-bold" data-testid="mrp-run-now-button">
            <Play size={13} className="mr-1" /> {t("mrp.runNow")}
          </Button>
        )}
      </PageHeader>

      {can("admin") && (
        <div className="border bg-card p-4 mb-4 flex items-center gap-6 flex-wrap" data-testid="mrp-schedule-section">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5">
            <Clock size={12} /> {t("mrp.schedule")}
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={schedule.enabled} onCheckedChange={(v) => saveSchedule({ enabled: v })} data-testid="mrp-schedule-switch" />
            <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mrp.auto")}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mrp.interval")}</Label>
            <Input type="number" min="1" value={schedule.interval_hours}
              onChange={(e) => setSchedule({ ...schedule, interval_hours: e.target.value })}
              onBlur={() => saveSchedule({})}
              className="rounded-none h-8 text-xs w-20" data-testid="mrp-interval-input" />
          </div>
          <span className="text-[10px] text-muted-foreground" data-testid="mrp-last-run">
            {t("mrp.lastRun")}: {schedule.last_run ? fmtDate(schedule.last_run) : "—"}
          </span>
        </div>
      )}

      {groups.length === 0 ? <Empty text={t("mrp.none")} /> : groups.map((g, gi) => (
        <div key={gi} className="border bg-card p-4 mb-4" data-testid={`mrp-group-${gi}`}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-xs font-black uppercase tracking-wide">
                {g.supplier_name || t("mrp.noSupplier")}
                {g.supplier_name && <span className="ml-2 text-[9px] font-bold text-muted-foreground border px-1.5 py-0.5">{g.currency}</span>}
              </div>
              {!g.supplier_name && <div className="text-[10px] text-muted-foreground">{t("mrp.assignHint")}</div>}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold tabular-nums">{fmtMoney(g.est_total)} MXN</span>
              {g.supplier_name && can("admin", "buyer") && (
                <Button size="sm" onClick={() => generatePo(g)} className="rounded-none text-xs font-bold"
                  data-testid={`mrp-generate-po-${gi}`}>
                  <ShoppingCart size={13} className="mr-1" /> {t("mrp.generatePo")}
                </Button>
              )}
              {!g.supplier_name && can("admin", "buyer", "warehouse") && (
                <Button size="sm" variant="outline" onClick={() => generateReq(g)} className="rounded-none text-xs font-bold"
                  data-testid={`mrp-generate-req-${gi}`}>
                  <FileText size={13} className="mr-1" /> {t("al.createReq")}
                </Button>
              )}
            </div>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr>
                <th>{t("common.item")}</th><th>{t("common.description")}</th>
                <th className="num">{t("al.minStock")}</th><th className="num">{t("al.onHand")}</th>
                <th className="num">{t("mrp.onOrder")}</th><th className="num">{t("al.shortage")}</th>
                <th className="num">{t("common.cost")}</th><th className="num">{t("al.estTotal")}</th>
              </tr></thead>
              <tbody>
                {g.lines.map((l) => (
                  <tr key={l.item_number}>
                    <td className="font-bold">{l.item_number}</td>
                    <td>{l.description}</td>
                    <td className="num">{fmtQty(l.min_stock)}</td>
                    <td className="num font-bold text-destructive">{fmtQty(l.on_hand)}</td>
                    <td className="num">{fmtQty(l.on_order)}</td>
                    <td className="num font-bold">{fmtQty(l.shortage)} {l.uom}</td>
                    <td className="num">{fmtMoney(l.est_cost)}</td>
                    <td className="num">{fmtMoney(l.est_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="mt-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("mrp.runs")}</div>
        {runs.length === 0 ? <div className="text-xs text-muted-foreground">{t("common.noData")}</div> : (
          <div className="tbl-wrap" data-testid="mrp-runs-table">
            <table className="tbl">
              <thead><tr>
                <th>{t("common.date")}</th><th>{t("mrp.mode")}</th><th>{t("common.user")}</th>
                <th className="num">OCs</th><th>{t("po.title")}</th>
              </tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td className="text-muted-foreground">{fmtDate(r.run_at)}</td>
                    <td><span className="uppercase text-[10px] font-bold">{r.mode === "auto" ? "Auto" : "Manual"}</span></td>
                    <td>{r.by}</td>
                    <td className="num font-bold">{r.po_count}</td>
                    <td className="text-muted-foreground">{r.created.map((c) => c.number + (c.email_status === "sent" ? " ✉" : "")).join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
